import React from "react";
import { Box, Stack } from "@mui/material";
import { ParlorDetailProps, ClickEventType } from "../../../types";
import { ColorSystemProps } from "../../../constants";
import { customEvent } from "../../../utils";
import { ColorIcon } from "../../common/Icons";

export function GTypeBinsoPriceSummary({
  defaultParlor,
  mainColor,
  ctaHref,
  ctaText,
  event,
}: {
  defaultParlor: ParlorDetailProps;
  mainColor: ColorSystemProps;
  ctaHref?: string;
  ctaText?: string;
  event?: ClickEventType;
}) {
  const binsos = [...(defaultParlor.binsos || [])].sort(
    (a, b) => (a.charge || 0) - (b.charge || 0),
  );
  const avg = defaultParlor.binso_average_charge;

  if (!binsos.length) return null;

  const minPy = Math.min(...binsos.map((b) => b.pyeong || 0));
  const maxPy = Math.max(...binsos.map((b) => b.pyeong || 0));

  return (
    <Stack gap="0" padding="56px 20px">
      <Box
        component="h2"
        className="h2"
        style={{
          fontSize: "24px",
          fontWeight: 700,
          marginTop: "14px",
          color: "#252525",
        }}
      >
        빈소 사용료 상세 내역
      </Box>
      <Box
        component="p"
        style={{
          marginTop: "14px",
          fontSize: "16px",
          lineHeight: 1.75,
          color: "#535353",
          maxWidth: "720px",
        }}
      >
        총 {binsos.length}개 빈소({minPy}~{maxPy}평)를 운영합니다.{" "}
        <strong>3일 기준 금액</strong>은 1일 사용료의 3배이며, 실제 사용 일수에
        따라 달라질 수 있습니다.
      </Box>

      <Box
        style={{
          marginTop: "28px",
          overflowX: "auto",
          borderRadius: "14px",
          border: "1px solid #e9e9e9",
        }}
      >
        <Box
          component="table"
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "14px",
          }}
        >
          <Box
            component="thead"
            style={{ background: mainColor.c100 }}
          >
            <tr>
              {["빈소", "평형", "사용료"].map((col) => (
                <Box
                  key={col}
                  component="th"
                  style={{
                    padding: "14px 16px",
                    textAlign: col === "빈소" ? "center" : "right",
                    fontWeight: 700,
                    fontSize: "13px",
                    color: mainColor.c500,
                    whiteSpace: "nowrap",
                    borderBottom: `2px solid ${mainColor.c200}`,
                  }}
                >
                  {col}
                </Box>
              ))}
            </tr>
          </Box>
          <Box component="tbody">
            {binsos.map((b, i) => {
              const isSpecial = (b.item || "").includes("특");
              const type = isSpecial ? "특실" : "일반실";
              const num = String(i + 1).padStart(2, "0");
              const charge = b.charge || 0;
              const isEven = i % 2 === 1;
              return (
                <Box
                  key={i}
                  component="tr"
                  style={{
                    background: isEven ? "#fafafa" : "#fff",
                    borderBottom: i === binsos.length - 1 ? "none" : "1px solid #f0f0f0",
                  }}
                >
                  <Box
                    component="td"
                    style={{
                      padding: "16px",
                      textAlign: "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Box
                      style={{
                        fontWeight: 800,
                        fontSize: "13px",
                        color: mainColor.c500,
                        letterSpacing: "0.08em",
                      }}
                    >
                      {num}
                    </Box>
                    <Box
                      component="span"
                      style={{
                        display: "inline-block",
                        marginTop: "4px",
                        padding: "2px 8px",
                        borderRadius: "999px",
                        fontSize: "11px",
                        fontWeight: 600,
                        color: isSpecial ? mainColor.c500 : "#8f8f8f",
                        background: isSpecial ? mainColor.c100 : "#f6f4f4",
                      }}
                    >
                      {type}
                    </Box>
                  </Box>
                  <Box
                    component="td"
                    style={{
                      padding: "16px",
                      textAlign: "right",
                      fontWeight: 700,
                      fontSize: "15px",
                      color: "#252525",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {b.pyeong || 0}평
                    <Box
                      component="div"
                      style={{
                        fontSize: "12px",
                        fontWeight: 400,
                        color: "#8f8f8f",
                        marginTop: "2px",
                      }}
                    >
                      {b.item || "—"}
                    </Box>
                  </Box>
                  <Box
                    component="td"
                    style={{
                      padding: "16px",
                      textAlign: "right",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Box
                      style={{
                        fontWeight: 800,
                        fontSize: "16px",
                        color: mainColor.c500,
                        fontFeatureSettings: '"tnum" on',
                        marginBottom: "4px",
                      }}
                    >
                      1일 {charge.toLocaleString("ko-KR")}원
                    </Box>
                    <Box
                      style={{
                        fontSize: "12px",
                        color: "#8f8f8f",
                        fontFeatureSettings: '"tnum" on',
                      }}
                    >
                      3일 {(charge * 3).toLocaleString("ko-KR")}원
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {avg ? (
        <Box
          component="p"
          style={{
            marginTop: "16px",
            fontSize: "12px",
            color: "#8f8f8f",
            lineHeight: 1.5,
          }}
        >
          * 빈소 평균 사용료는{" "}
          <strong>{avg.toLocaleString("ko-KR")}원/1일</strong> 수준입니다. 실제
          부과 금액은 빈소 선택과 사용 일수에 따라 달라집니다.
        </Box>
      ) : null}

      {ctaHref && (
        <Box style={{ marginTop: "24px" }}>
          {ctaText && (
            <Box
              style={{
                fontSize: "13px",
                color: "#8f8f8f",
                marginBottom: "10px",
                textAlign: "center",
                whiteSpace: "pre-wrap",
              }}
            >
              {ctaText}
            </Box>
          )}
          <Box
            component="a"
            href={ctaHref}
            onClick={event ? () => customEvent(event) : undefined}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              padding: "16px",
              borderRadius: "12px",
              fontSize: "16px",
              fontWeight: 700,
              background: mainColor.c400,
              color: "#fff",
              textDecoration: "none",
            }}
          >
            <span>장례 비용 확인하기</span>
            <ColorIcon name="arrow_right_small" color="#fff" size={18} />
          </Box>
          <Box
            component="p"
            style={{
              marginTop: "10px",
              fontSize: "12px",
              color: "#888",
              lineHeight: 1.5,
              textAlign: "center",
            }}
          >
            ※ e하늘장사정보시스템을 기반으로 고이장례연구소에서 예상 값을 산출하여 제공해요
          </Box>
        </Box>
      )}
    </Stack>
  );
}
